import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Paper,
  Alert,
  Button,
} from '@mui/material';

import { usePaymentMethod } from '../../stores';

import EditPaymethod from '../../components/paymethod/EditPaymethod';
import LoadingWrapper from '../../components/LoadingWrapper';

import { DefaultNamesForPaymentMethodConfigTypes } from '../../model/PaymentMethodConfig';
import { usePaymentMethods } from '../../stores/FetcherStores/PaymentMethodFetcher';
import useCompanyId from '../../hooks/useCompanyId';

const PaymentMethodsList = observer(() => {
  const companyId = useCompanyId();
  const [editPaymethodOpen, setEditPaymethodOpen] = useState<boolean>(false);

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>('');

  const { message, resetMessage } = usePaymentMethod();

  const { data: paymentMethods, isLoading } = usePaymentMethods(companyId);

  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      resetMessage();
    };
  }, []);

  const handleOpenEdit = (paymentMethodId: string) => {
    if (!paymentMethodId) return;

    setSelectedPaymentMethodId(paymentMethodId);
    setEditPaymethodOpen(true);
  };

  const handleCloseEdit = () => {
    setSelectedPaymentMethodId('');
    setEditPaymethodOpen(false);
  };

  return (
    <Box>
      {message.text && (
        <Box sx={{ marginBottom: 2, width: '100%' }}>
          <Alert severity={message.severity}>{message.text}</Alert>
        </Box>
      )}
      <Typography variant="h1" gutterBottom>
        {t('paymethodScreen.paymethod')}
      </Typography>
      <Paper sx={{ marginTop: 2, marginBottom: 2, padding: '1rem' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'end' }}>
          <Button
            variant="contained"
            disabled={isLoading}
            onClick={() => setEditPaymethodOpen(true)}
          >
            {t('paymethodScreen.addPaymethod')}
          </Button>
        </Box>
        <LoadingWrapper loading={!paymentMethods}>
          <TableContainer sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('paymethodScreen.name')}</TableCell>
                  <TableCell>{t('paymethodScreen.paymethodType')}</TableCell>
                  <TableCell>{t('paymethodScreen.active')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentMethods &&
                  paymentMethods.map((paymentMethod) => (
                    <TableRow
                      key={paymentMethod.id}
                      onClick={() => handleOpenEdit(paymentMethod.id)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        {paymentMethod.name
                          ? paymentMethod.name
                          : DefaultNamesForPaymentMethodConfigTypes[paymentMethod.type]}
                      </TableCell>
                      <TableCell>
                        {DefaultNamesForPaymentMethodConfigTypes[paymentMethod.type] ??
                          paymentMethod.type}
                      </TableCell>
                      <TableCell>
                        {paymentMethod.active
                          ? t('paymethodScreen.active')
                          : t('paymethodScreen.inactive')}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </LoadingWrapper>
      </Paper>
      <EditPaymethod
        paymentMethodId={selectedPaymentMethodId}
        open={editPaymethodOpen}
        onClose={handleCloseEdit}
      />
    </Box>
  );
});

export default PaymentMethodsList;
